import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Page, Card, Tabs } from '@shopify/polaris';
import { useUrl } from '../../shared/util/hanldesave';
import { filtersToApiUrl, alphabetize, getUserOrg, fetchTableData, getRequest } from '../../shared/components/functions';
import InvoicesInTable from './components/invoicesInTable';
import InvoiceLinesTable from './components/invoiceLinesTable';
import CostBreakdownTable from './components/costBreakdownTable';
import InvoiceInCollection from './components/invoiceInCollection';
import { useAuthStore, useLookupStore } from '../../context/useStore';
import { useDebounce } from '../../shared/components/inputs/useDebounce';
import { getUserOptions } from '../../shared/components/listFilter';
import { STATUS_CHOICES } from '../../shared/components/approvals/approvalStatus';

export default function InvoicesInPage() {
  const url = useUrl();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const organisations = useLookupStore((state) => state.organisations);
  const users = useLookupStore((state) => state.users);
  const purchasetypes = useLookupStore((state) => state.purchasetypes);
  const businessunits = useLookupStore((state) => state.businessunits);
  const incasseren = useLookupStore((state) => state.incasseren);

  // Load purchasetypes on mount
  const setPurchasetypes = useLookupStore((state) => state.setPurchasetypes);
  useEffect(() => {
    setPurchasetypes();
  }, [setPurchasetypes]);

  const userOrg = user && getUserOrg(organisations, user.ORGANISATION_NAME);
  const userChoices = users
    .filter((item) => item.UserType !== 4 && item.UserType !== 7 && item.Vennootschap === userOrg)
    .sort((a, b) => alphabetize(a, b, 'Afkorting'))
    .map((item) => ({ key: item.GebruikerID, value: item.GebruikerID, label: `${item.Afkorting} (${item.Gebruikernaam})` }));
  const filteredUsers = getUserOptions(users, userOrg);

  const purchaseTypeChoices = purchasetypes.map((item) => ({ key: item.BestellingTypeID, value: item.BestellingTypeID, label: item.BestellingType }));
  const businessUnitChoices = businessunits
    .filter((bu) => bu.IsActive)
    .sort((a, b) => (a.SortOrder > b.SortOrder ? 1 : a.SortOrder < b.SortOrder ? -1 : a.Name.localeCompare(b.Name)))
    .map((bu) => ({ key: bu.BusinessUnitID, value: bu.BusinessUnitID, label: `${bu.Code} (${bu.Name})` }));
  const incasserenChoices = incasseren.map((item) => ({ key: item.IncasserenID, value: item.Incasseren, label: item.Incasseren }));

  const [items, setItems] = useState([]);
  const [invoiceLines, setInvoiceLines] = useState([]);
  const [invoiceCosts, setInvoiceCosts] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(url.get('limit') ? parseInt(url.get('limit')) : 100);
  const [invoiceCollectionModalOpen, setInvoiceCollectionModalOpen] = useState(false);
  const [collectionInvoice, setCollectionIvoice] = useState({});
  const [fullInvoiceCollection, setFullInvoiceCollection] = useState(false); //full if fully paid
  const [selectedTab, setSelectedTab] = useState(url.get('tab') ? parseInt(url.get('tab')) : 0);
  const [filterLoading, setFilterLoading] = useState(false);
  const [filters, setFilters] = useState({
    q: { type: 'text', name: 'Q', value: url.get('q') || '' },
    supplier: { type: 'text', name: 'Supplier', value: url.get('supplier') || '' },
    note: { type: 'text', name: 'Note', value: url.get('note') || '' },
    dateInFrom: { type: 'date', name: 'Date in from', value: url.get('dateInFrom') || '' },
    dateInTo: { type: 'date', name: 'Date in to', value: url.get('dateInTo') || '' },
    dateFrom: { type: 'date', name: 'Date from', value: url.get('dateFrom') || '' },
    dateTill: { type: 'date', name: 'Date till', value: url.get('dateTill') || '' },
    dateExpireFrom: { type: 'date', name: 'Date expire from', value: url.get('dateExpireFrom') || '' },
    dateExpireTo: { type: 'date', name: 'Date expire to', value: url.get('dateExpireTo') || '' },
    dateInvoice: { type: 'date', name: 'Date invoice', value: url.get('dateInvoice') || '' },
    reference: { type: 'text', name: 'Reference', value: url.get('reference') || '' },
    value: { type: 'range', name: 'Value', value: url.get('value') ? JSON.parse(url.get('value')) : null, rangeBottom: 0, rangeTop: 1000000, prefix: '€' },
    valueOpen: { type: 'range', name: 'Value open', value: url.get('valueOpen') ? JSON.parse(url.get('valueOpen')) : null, rangeBottom: 0, rangeTop: 1000000, prefix: '€' },
    datePayment: { type: 'date', name: 'Date payment', value: url.get('datePayment') || '' },
    approval: { type: 'multiple', name: 'Approver', value: url.get('approval') ? JSON.parse(url.get('approval')) : null, choices: userChoices },
    approvalStatus: { type: 'multiple', name: 'Approval status', value: url.get('approvalStatus') ? JSON.parse(url.get('approvalStatus')) : null, choices: STATUS_CHOICES },
    ToApprove: { type: 'none', value: url.get('ToApprove') || null, name: 'To approve' },
    tab: { type: 'none', value: url.get('tab') || null, name: 'Tab' },
    Open: { type: 'none', value: url.get('Open') || null, name: 'Open' },
    GaatVervallen: { type: 'none', value: url.get('GaatVervallen') || null, name: 'Gaat Vervallen' },
    Vervallen: { type: 'none', value: url.get('Vervallen') || null, name: 'Vervallen' },
    Paid: { type: 'none', value: url.get('Paid') || null, name: 'Paid' },
    PaymentSent: { type: 'none', value: url.get('PaymentSent') || null, name: 'Sent to bank' },
    owner: { type: 'multiple', name: 'Owner', value: url.get('owner') ? JSON.parse(url.get('owner')) : null, choices: filteredUsers },
    businessUnit: { type: 'multiple', name: 'Business unit', value: url.get('businessUnit') ? JSON.parse(url.get('businessUnit')) : null, choices: businessUnitChoices },
    incasseren: { type: 'multiple', name: 'Incasseren', value: url.get('incasseren') ? JSON.parse(url.get('incasseren')) : null, choices: incasserenChoices },
    // Invoice line specific filters
    tekstlijn: { type: 'text', name: 'Description', value: url.get('tekstlijn') || '' },
    artikelCode: { type: 'text', name: 'Article Code', value: url.get('artikelCode') || '' },
    vp: { type: 'range', name: 'Unit Price', value: url.get('vp') ? JSON.parse(url.get('vp')) : null, rangeBottom: 0, rangeTop: 10000, prefix: '€' },
    aantal: { type: 'range', name: 'Quantity', value: url.get('aantal') ? JSON.parse(url.get('aantal')) : null, rangeBottom: 0, rangeTop: 10000, prefix: '' },
    btw: { type: 'range', name: 'VAT Amount', value: url.get('btw') ? JSON.parse(url.get('btw')) : null, rangeBottom: 0, rangeTop: 10000, prefix: '€' },
    muntVP: { type: 'text', name: 'Currency', value: url.get('muntVP') || '' },
    cost: { type: 'range', name: 'Cost', value: url.get('cost') ? JSON.parse(url.get('cost')) : null, rangeBottom: 0, rangeTop: 1000000, prefix: '€' },
    costType: { type: 'multiple', name: 'Cost Type', value: url.get('costType') ? JSON.parse(url.get('costType')) : null, choices: purchaseTypeChoices }
  });

  useEffect(() => {
    // Refresh choices when purchasetypes load
    setFilters((prev) => ({
      ...prev,
      costType: { ...prev.costType, choices: purchaseTypeChoices },
      businessUnit: { ...prev.businessUnit, choices: businessUnitChoices },
      incasseren: { ...prev.incasseren, choices: incasserenChoices }
    }));
  }, [purchasetypes, businessunits, incasseren]);

  const fetchData = async (abortController) => await fetchTableData(page, '/api/invoices_in', setItems, filters, abortController, setFilterLoading, limit);
  const debouncedFetchData = useDebounce(fetchData);

  const fetchInvoiceLines = async (abortController) => {
    try {
      const response = await fetch(`/api/invoice_lines?page=${page}&limit=${limit}&${filtersToApiUrl(filters)}`, {
        signal: abortController.signal
      });
      if (response.ok) {
        const data = await response.json();
        setInvoiceLines(data);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching invoice lines:', error);
      }
    }
  };

  const fetchInvoiceCosts = async (abortController) => {
    try {
      const response = await fetch(`/api/invoices_in/costs?page=${page}&limit=${limit}&${filtersToApiUrl(filters)}`, {
        signal: abortController.signal
      });
      if (response.ok) {
        const data = await response.json();
        setInvoiceCosts(data);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching invoice costs:', error);
      }
    }
  };

  useEffect(() => {
    const abortController = new AbortController();
    const fetchDataWithAbort = async () => {
      if (selectedTab === 5) {
        // Invoice lines tab
        await fetchInvoiceLines(abortController);
      } else if (selectedTab === 6) {
        // Invoice costs tab
        await fetchInvoiceCosts(abortController);
      } else {
        await debouncedFetchData(abortController);
      }
    };
    fetchDataWithAbort();
    return () => abortController.abort(); // Cleanup, abort controller when page/filters change
  }, [page, filters, selectedTab, limit]);

  async function fetchInvoices() {
    const data = await getRequest(`/api/invoices_in?page=${page}&limit=${limit}&${filtersToApiUrl(filters)}`);
    setItems(data);
  }

  const tabIds = [
    'all-invoices',
    'all-invoices-open',
    'all-invoices-going-to-expire',
    'all-invoices-vervallen',
    'all-invoices-paid',
    'invoice-lines',
    'cost-breakdown',
    'all-invoices-to-approve',
    'all-invoices-sent-to-bank'
  ];

  let table = [
    <InvoicesInTable
      key={tabIds.map((id) => id)}
      items={items}
      page={page}
      setPage={setPage}
      filters={filters}
      setFilters={setFilters}
      navigate={navigate}
      setInvoiceCollectionModalOpen={setInvoiceCollectionModalOpen}
      setCollectionIvoice={setCollectionIvoice}
      setFullInvoiceCollection={setFullInvoiceCollection}
      filterLoading={filterLoading}
      refreshData={fetchInvoices}
    />
  ];

  let invoiceLinesTable = [
    <InvoiceLinesTable key="invoice-lines-table" items={invoiceLines} page={page} setPage={setPage} filters={filters} setFilters={setFilters} navigate={navigate} filterLoading={filterLoading} />
  ];

  let costBreakdownTable = [
    <CostBreakdownTable
      key="cost-breakdown-table"
      items={invoiceCosts}
      page={page}
      setPage={setPage}
      filters={filters}
      setFilters={setFilters}
      navigate={navigate}
      filterLoading={filterLoading}
      limit={limit}
      setLimit={setLimit}
      purchasetypes={purchasetypes}
      refreshData={() => fetchInvoiceCosts(new AbortController())}
    />
  ];

  let tabs;
  tabs = [
    {
      id: tabIds[0],
      content: 'All',
      panelID: 'all-invoices-content',
      comp: table,
      filter: ''
    },
    {
      id: tabIds[1],
      content: 'Open',
      panelID: 'all-invoices-open',
      comp: table,
      filter: 'Open'
    },
    {
      id: tabIds[2],
      content: 'Expiring soon (2 days)',
      panelID: 'all-invoices-expired',
      comp: table,
      filter: 'GaatVervallen'
    },
    {
      id: tabIds[3],
      content: 'Expired',
      panelID: tabIds[3],
      comp: table,
      filter: 'Vervallen'
    },
    {
      id: tabIds[4],
      content: 'Paid',
      panelID: tabIds[4],
      comp: table,
      filter: 'Paid'
    },
    {
      id: tabIds[5],
      content: 'Invoice Lines',
      panelID: 'invoice-lines',
      comp: invoiceLinesTable,
      filter: ''
    },
    {
      id: tabIds[6],
      content: 'Cost Breakdown',
      panelID: 'cost-breakdown',
      comp: costBreakdownTable,
      filter: ''
    },
    {
      id: tabIds[7],
      content: 'To approve',
      panelID: tabIds[7],
      comp: table,
      filter: 'ToApprove'
    },
    {
      // invoices with a pending Ponto payment (sent to the bank, not matched yet)
      id: tabIds[8],
      content: 'Sent to bank',
      panelID: tabIds[8],
      comp: table,
      filter: 'PaymentSent'
    }
  ];

  const handleTabChange = useCallback(
    (selectedTabIndex) => {
      setSelectedTab(selectedTabIndex);
      let removeTabsFilters = {};
      for (let i = 1; i < tabs.length; i++) {
        if (tabs[i].filter === tabs[selectedTabIndex].filter) {
          removeTabsFilters[tabs[i].filter] = { type: 'none', value: '1' };
        } else {
          removeTabsFilters[tabs[i].filter] = { type: 'none', value: null };
        }
      }
      setFilters(
        (filters) => ({ ...filters, tab: { type: 'none', value: selectedTabIndex }, ...removeTabsFilters }),
        navigate(
          {
            pathname: window.location.pathname,
            search: `${filtersToApiUrl({ ...filters, tab: { type: 'none', value: selectedTabIndex }, ...removeTabsFilters })}`
          },
          { replace: true }
        )
      );
    },
    [navigate, filters, tabs]
  );

  const autoProcessEmails = {
    schuilenburg: 'invoices@admin.schuilenburg.com',
    aha: 'invoices-aha@admin.schuilenburg.com',
    jadibo: 'invoices-jadibo@admin.schuilenburg.com',
    adriaens: 'invoices-adriaens@admin.schuilenburg.com',
    localhost: 'invoices-localhost@admin.schuilenburg.com' // for testing
  };

  const getAutoProcessEmail = () => {
    const url = window.location.href;
    const keys = Object.keys(autoProcessEmails);
    for (let i = 0; i < keys.length; i++) {
      if (url.includes(keys[i])) return autoProcessEmails[keys[i]];
    }
    return '';
  };

  return (
    <Page title="Invoices in" subtitle={`Upload automatically by emailing to ${getAutoProcessEmail()}`} fullWidth primaryAction={{ content: 'New incoming invoice', url: '/invoices_in/new' }}>
      <InvoiceInCollection
        modalOpen={invoiceCollectionModalOpen}
        setModalOpen={setInvoiceCollectionModalOpen}
        collectionInvoice={collectionInvoice}
        fetchData={fetchInvoices}
        fullInvoiceCollection={fullInvoiceCollection}
      />
      <Card>
        <Tabs tabs={tabs} selected={selectedTab} onSelect={handleTabChange}>
          {tabs[selectedTab].comp}
        </Tabs>
      </Card>
    </Page>
  );
}
